import { client } from '../setup';
import {
  QueryCreator,
  CriteriaCreator,
  MutationCreator,
  SingleRecordMutationCreator,
} from './base';
import { createAcceptedProperties } from '../helpers';
import { DevicesQuery } from './devices';
import {
  AppliancesQuery, getAppliances, updateAppliance,
} from './appliances';
import { dataRegex } from '../helpers';
import sourcesData from '../mocks/sources.json';
import {
  deleteStream, getStreams, StreamsQuery,
} from './streams';
import * as is from 'is';
import { getLogger } from '@utils/general';
const logger = getLogger('src.utils.graphQL.creators.sources');

const acceptedReturnProperties =
  createAcceptedProperties(
    'id',
    'name',
    'management_address',
    'hostname',
    'public_address',
    'device.id',
    'device.name',
    'device.network_address',
    'appliance.id',
    'appliance.name',
    'appliance.management_address',
    'path',
    'description',
    'category',
    'port',
    'uri',
    'sdp_input',
    'scheme',
    'streams.id',
    'streams.name'
  );

const acceptedCriteriaProperties = {
  name: { type: 'string' },
  description: { type: 'string', triplequoted: true },
  scheme: { type: 'string' },
  category: { type: 'enum' },
  path: { type: 'string' },
  port: { type: 'number' },
  sdpInput: { type: 'string', triplequoted: true },
  deviceId: { type: 'string' },
  zoneId: { type: 'string' },
  applianceId: { type: 'string' },
  sourceId: { type: 'string' },
};

class SourceCriteria extends CriteriaCreator {
  #deviceQuery = new DevicesQuery();
  #applianceQuery = new AppliancesQuery();
  #streamQuery = new StreamsQuery('streams', null, { includeSourceQuery: false });

  constructor(criteria = {}) {
    super(criteria);
  }

  get acceptedCriteria() {
    return acceptedCriteriaProperties;
  }
  addReturnProperties(...properties) {
    const deviceProperties = [];
    const applianceProperties = [];
    const streamProperties = [];
    const otherProperties = [];

    for (const property of properties) {
      if (property.startsWith('device')) {
        const [, deviceProperty] = property.split('.');
        deviceProperties.push(deviceProperty);
        continue;
      }
      if (property.startsWith('appliance')) {
        const [, applianceProperty] = property.split('.');
        applianceProperties.push(applianceProperty);
        continue;
      }
      if (property.startsWith('streams')) {
        const [, streamProperty] = property.split('.');
        streamProperties.push(streamProperty);
        continue;
      }
      otherProperties.push(property);
    }
    this.#deviceQuery.addReturnProperties(...deviceProperties);
    this.#applianceQuery.addReturnProperties(...applianceProperties);
    this.#streamQuery.addReturnProperties(...streamProperties);

    super.addReturnProperties(...otherProperties);
    return this;
  }

  returnSyntax() {
    const syntax = super.returnSyntax();
    if (syntax === '') {
      return syntax;
    }

    const [, inner] = syntax.match(dataRegex);
    const deviceSyntax = this.#deviceQuery.returnSyntax();
    const applianceSyntax = this.#applianceQuery.returnSyntax();
    const streamSyntax = this.#streamQuery.returnSyntax();

    if (deviceSyntax === '' && applianceSyntax === '' && streamSyntax === '') {
      return syntax;
    }

    return `data {
      ${inner}\n${deviceSyntax.replace(/^data/, 'device')}
      ${applianceSyntax.replace(/^data/, 'appliance')}
      ${streamSyntax.replace(/^data/, 'streams')}
    }`;
  }
}

class SourceQueryCriteria extends SourceCriteria {
  get acceptedCriteria() {
    return { ...acceptedCriteriaProperties, category: { type: 'string' } };
  }
}

export class SourcesQuery extends QueryCreator {
  #deviceQuery = null;
  #applianceQuery = null;
  #streamQuery = null;

  constructor(queryType = 'sources', options = {}) {
    super(queryType, SourceQueryCriteria);


    // Default behavior: Instantiate deviceQuery unless explicitly told not to
    if (options.includeDeviceQuery === false) {
      this.#deviceQuery = null; // Disable deviceQuery if specified
    } else {
      this.#deviceQuery = new DevicesQuery(); // Default: Instantiate deviceQuery
    }

    // Default behavior: Instantiate applianceQuery unless explicitly told not to
    if (options.includeApplianceQuery === false) {
      this.#applianceQuery = null; // Disable applianceQuery if specified
    } else {
      this.#applianceQuery = new AppliancesQuery();// Default: Instantiate applianceQuery
    }

    // Default behavior: Instantiate includeStreamQuery unless explicitly told not to
    if (options.includeStreamQuery === false) {
      this.#streamQuery = null; // Disable includeStreamQuery if specified
    } else {
      this.#streamQuery = new StreamsQuery('streams', null, { includeSourceQuery: false });// Default: Instantiate includeStreamQuery
    }
  }

  // Optionally expose the deviceQuery as a getter if you need to access it
  get deviceQuery() {
    return this.#deviceQuery;
  }

  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }

  addReturnProperties(...properties) {
    const deviceProperties = [];
    const applianceProperties = [];
    const streamProperties = [];
    const otherProperties = [];

    for (const property of properties) {
      if (property.startsWith('device')) {
        const [, deviceProperty] = property.split('.');
        deviceProperties.push(deviceProperty);
        continue;
      }
      if (property.startsWith('appliance')) {
        const [, applianceProperty] = property.split('.');
        applianceProperties.push(applianceProperty);
        continue;
      }
      if (property.startsWith('streams')) {
        const [, streamProperty] = property.split('.');
        streamProperties.push(streamProperty);
        continue;
      }
      otherProperties.push(property);
    }
    if (this.#deviceQuery) {
      this.#deviceQuery.addReturnProperties(...deviceProperties);
    }

    if (this.#applianceQuery) {
      this.#applianceQuery.addReturnProperties(...applianceProperties);
    }

    if (this.#streamQuery) {
      this.#streamQuery.addReturnProperties(...streamProperties);
    }

    super.addReturnProperties(...otherProperties);

    return this;
  }

  returnSyntax() {
    const syntax = super.returnSyntax();
    if (syntax === '') {
      return syntax;
    }

    const [, inner] = syntax.match(dataRegex);
    let deviceSyntax = '';
    let streamSyntax = '';
    let applianceSyntax = '';

    if (this.#deviceQuery) {
      deviceSyntax = this.#deviceQuery.returnSyntax();
    }

    if (this.#applianceQuery) {
      applianceSyntax = this.#applianceQuery.returnSyntax();
    }

    if (this.#streamQuery) {
      streamSyntax = this.#streamQuery.returnSyntax();
    }

    if (deviceSyntax === '' && applianceSyntax === '' && streamSyntax === '') {
      return syntax;
    }

    return `data {
    ${inner}\n${deviceSyntax.replace(/^data/, 'device')}
    ${applianceSyntax.replace(/^data/, 'appliance')}
    ${streamSyntax.replace(/^data/, 'streams')}
    }`;
  }
}

export function getSources(id, criteria = {}) {
  let query;
  if (id) {
    query = new SourcesQuery('source');
    query.setId(id);
  } else {
    query = new SourcesQuery();
    if (!is.empty(criteria)) {
      Object.keys(criteria).forEach((key) => {
        query.addCriteria(key, criteria[key]);
      });
    }
  }

  return {
    query,
    execute() {
      return /^dev(?:elop(?:ment)?)?$/i.test(import.meta.env.VITE_ENV)
        ? Promise.resolve().then(() => {
          const sources = id
            ? sourcesData.find((source) => source.id === id)
            : criteria
              ? sourcesData.filter((source) => {
                return Object.keys(criteria).every((key) => source[key] === criteria[key]);
              })
              : sourcesData;
          return { data: sources };
        })
        : client.query(query.name, query.toString());
    },
  };
}

export class SingleSourceMutation extends SingleRecordMutationCreator {
  constructor(queryType = 'create_source', criteria = {}) {
    super(queryType, SourceCriteria);
    this.setCriteria(criteria);
  }
  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }

  #deviceQuery = new DevicesQuery();
  #applianceQuery = new AppliancesQuery();

  addReturnProperties(...properties) {
    const deviceProperties = [];
    const applianceProperties = [];
    const otherProperties = [];
    for (const property of properties) {
      if (property.startsWith('device')) {
        const [, deviceProperty] = property.split('.');
        deviceProperties.push(deviceProperty);
        continue;
      }
      if (property.startsWith('appliance')) {
        const [, applianceProperty] = property.split('.');
        applianceProperties.push(applianceProperty);

        continue;
      }
      otherProperties.push(property);
    }

    this.#deviceQuery.addReturnProperties(...deviceProperties);
    this.#applianceQuery.addReturnProperties(...applianceProperties);
    super.addReturnProperties(...otherProperties);
    return this;
  }

  returnSyntax() {
    const deviceSyntax = this.#deviceQuery.returnSyntax();
    const applianceSyntax = this.#applianceQuery.returnSyntax();

    if (deviceSyntax === '' && applianceSyntax === '') {
      return '';
    }

    return `${deviceSyntax.replace(/^data/, 'device')}${applianceSyntax.replace(/^data/, 'appliance')}`;
  }
}

export class SourcesMutation extends MutationCreator {
  constructor(queryType = 'create_source', criteria = {}) {
    super(queryType, SourceCriteria);
    this.setCriteria(criteria);
  }
  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }
  #deviceQuery = new DevicesQuery();
  #applianceQuery = new AppliancesQuery();

  addReturnProperties(...properties) {
    const deviceProperties = [];
    const applianceProperties = [];
    const otherProperties = [];
    for (const property of properties) {
      if (property.startsWith('device')) {
        const [, deviceProperty] = property.split('.');
        deviceProperties.push(deviceProperty);
        continue;
      }
      if (property.startsWith('appliance')) {
        const [, applianceProperty] = property.split('.');
        applianceProperties.push(applianceProperty);

        continue;
      }
      otherProperties.push(property);
    }
    this.#deviceQuery.addReturnProperties(...deviceProperties);
    this.#applianceQuery.addReturnProperties(...applianceProperties);
    super.addReturnProperties(...otherProperties);
    return this;
  }

  returnSyntax() {
    // const syntax = super.returnSyntax();
    // @todo: Why is syntax an empty string? It should have name and id
    // the StringMutation class (ie 'this') has 2 #returnProperties, which is also weird.
    // One has id and name and one is empty
    // if (syntax === '') {
    // return syntax;
    // }
    const deviceSyntax = this.#deviceQuery.returnSyntax();
    const applianceSyntax = this.#applianceQuery.returnSyntax();

    if (deviceSyntax === '' && applianceSyntax === '') {
      // return syntax;
      return '';
    }

    return `${deviceSyntax.replace(/^data/, 'device')}${applianceSyntax.replace(/^data/, 'appliance')}`;
  }
};

export function saveSource(mutationType, input) {
  let mutation;
  mutation = new SourcesMutation(mutationType, input);
  return {
    mutation,
    execute() {
      return client.mutate(mutation.name, mutation.toString());
    },
  };
};

export function updateSource(mutationType, id, input) {
  let mutation;
  mutation = new SingleSourceMutation(mutationType, input);
  mutation.setId(id);

  return {
    mutation,
    execute() {
      return client.mutate(mutation.name, mutation.toString());
    },
  };
}

export const deleteSource = async (source) => {
  const { execute: executeSrcRemoval } = updateSource('remove_source', source.id);

  const { execute: executeGetStreams, query: streamQuery } = getStreams(null, { sourceId: source.id });
  streamQuery.addReturnProperties('id', 'name');

  const { execute: executeGetApp, query: applianceQuery } =
    getAppliances(null, { offline_source_id: source.id });
  applianceQuery.addReturnProperties('id', 'name', 'offline_source_id');

  try {
    if (source.category === 'Offline') {
      //if source is offline, get all appliances that have the source id as their offline_source_id
      const { error: getAppliancesError, data: appliances } = await executeGetApp();
      if (getAppliancesError) {
        throw new Error(getAppliancesError);
      }

      for (const appliance of appliances) {
        // disconnect source from chained appliances
        const nullOfflineSrc = { offline_source_id: null };
        const { execute: executeUpdateAppliance } = updateAppliance('update_appliance', appliance.id, nullOfflineSrc);
        const { error: updateApplianceError } = await executeUpdateAppliance();
        if (updateApplianceError) {
          throw new Error(updateApplianceError);
        }
      }
    }

    // Get all associated streams & delete them before deleting a source
    const { error: getStreamsError, data: streamsAssocWithSource } = await executeGetStreams();

    if (getStreamsError) {
      throw new Error(getStreamsError);
    }
    if (streamsAssocWithSource) {
      for (const stream of streamsAssocWithSource) {
        await deleteStream(stream);
      }
    }

    await executeSrcRemoval();
  } catch (error) {
    logger.error(error, `Failed to delete source ${source.id}:`);
    throw new Error(error);
  }
};